import React, { useEffect, useState } from 'react';
import { ethers } from 'ethers';
import { useNavigate } from 'react-router-dom';
import { useWallet } from '../../context/WalletContext';
import { useContracts } from '../../hooks/useContracts';
import { useUSDT } from '../../hooks/useUSDT';
import { useReferral } from '../../hooks/useReferral';
import { useNotify } from '../../context/NotificationContext';
import { parseError } from '../../utils/errorParser';
import { fmtUSDT } from '../../utils/format';
import ButtonLoader from '../common/ButtonLoader';

const RegisterForm = ({ onRegistered }) => {
  const { account } = useWallet();
  const { plans, coreStorage } = useContracts();
  const { balance, allowance, approveMax } = useUSDT();
  const { ref: capturedRef, clearRef } = useReferral();
  const notify = useNotify();
  const navigate = useNavigate();

  const [refId, setRefId] = useState('');
  const [refLocked, setRefLocked] = useState(false);
  const [price, setPrice] = useState(ethers.BigNumber.from(0));
  const [busy, setBusy] = useState(false);

  // Hydrate sponsor ID from referral hook (URL > localStorage)
  useEffect(() => {
    if (capturedRef && /^\d+$/.test(capturedRef)) {
      setRefId(capturedRef);
      setRefLocked(true);
    }
  }, [capturedRef]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!coreStorage?.read) return;
      try {
        const p = await coreStorage.read.getPackagePrice(1);
        if (!cancelled) setPrice(p);
      } catch {}
    };
    load();
    return () => { cancelled = true; };
  }, [coreStorage]);

  const onUnlock = () => {
    setRefLocked(false);
    clearRef();
  };

  const onRegister = async () => {
    if (!plans?.write) return notify.error('Wallet', 'Connect wallet first');
    if (balance.lt(price)) return notify.error('Insufficient', `Need ${fmtUSDT(price)} USDT`);

    setBusy(true);
    try {
      if (allowance.lt(price)) {
        notify.info('Approval', 'Approving USDT…');
        await approveMax();
        notify.success('Approved', 'USDT approval set');
      }

      // Empty / invalid sponsor → 0 (contract auto-routes to default sponsor)
      const refToSend = (refId && /^\d+$/.test(refId)) ? refId : '0';

      notify.info('Registering', 'Submitting transaction…');
      const tx = await plans.write.registerAndActivate(refToSend);
      await tx.wait();
      notify.success('Registered', 'Welcome to GalaxyXon!');

      clearRef();
      onRegistered && onRegistered();
      navigate('/dashboard', { replace: true });
    } catch (e) {
      notify.error('Registration failed', parseError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card card-yellow" style={{ maxWidth: 520, margin: '0 auto' }}>
      <div className="card-title">Register & Activate Genesis</div>
      <div style={{ marginTop: 6, color: 'var(--text-primary)', fontSize: 14 }}>
        Activate your Genesis package to start earning across 6 income streams.
      </div>

      <div style={{ display: 'flex', gap: 16, marginTop: 18, alignItems: 'baseline', flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>PACKAGE</div>
          <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 22 }}>Genesis</div>
        </div>
        <div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>PRICE</div>
          <div style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 22, color: 'var(--usdt-green)' }}>
            {fmtUSDT(price)} USDT
          </div>
        </div>
        <div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>CEILING</div>
          <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 22 }}>4×</div>
        </div>
      </div>

      <div style={{ marginTop: 16 }}>
        <label style={{ fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 1 }}>
          Sponsor ID {refLocked ? '(from referral link)' : '(optional)'}
        </label>
        <div style={{ position: 'relative', marginTop: 6 }}>
          <input
            type="text"
            inputMode="numeric"
            placeholder="Leave empty for default sponsor"
            value={refId}
            onChange={(e) => setRefId(e.target.value.replace(/[^0-9]/g, ''))}
            disabled={refLocked}
            style={refLocked ? { paddingRight: 86, opacity: 0.95 } : {}}
          />
          {refLocked && (
            <button
              type="button"
              onClick={onUnlock}
              style={{
                position: 'absolute',
                right: 6,
                top: '50%',
                transform: 'translateY(-50%)',
                fontSize: 11,
                fontWeight: 700,
                padding: '6px 10px',
                borderRadius: 6,
                background: 'var(--bg-charcoal-4)',
                color: 'var(--text-primary)',
                border: '1px solid var(--border-subtle)'
              }}
            >
              Change
            </button>
          )}
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>
          {refLocked
            ? `Linked to sponsor #${refId} from your referral link.`
            : <>Tip: open the site with <code>?ref=YOUR_ID</code> to autofill.</>}
        </div>
      </div>

      <button
        className="btn btn-primary btn-block btn-lg"
        style={{ marginTop: 18 }}
        onClick={onRegister}
        disabled={busy || !account}
      >
        {busy ? <ButtonLoader label="Processing" /> : 'Register & Activate'}
      </button>

      <div style={{ marginTop: 10, fontSize: 12, color: 'var(--text-muted)', display: 'flex', justifyContent: 'space-between' }}>
        <span>Your USDT: <strong style={{ color: 'var(--usdt-green)' }}>{fmtUSDT(balance)}</strong></span>
        <span>Approved: <strong style={{ color: 'var(--bnb-yellow)' }}>{allowance.gte(price) && price.gt(0) ? 'Yes' : 'Auto'}</strong></span>
      </div>
    </div>
  );
};

export default RegisterForm;
